import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Users, Loader2, CheckCircle2, XCircle, User, Heart, Download,
  UserMinus, UserCheck, Hash, Filter, Upload, FileSpreadsheet, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROSTER_STATUS = {
  tryout: { label: 'Try-out', color: 'bg-amber-600' },
  active: { label: 'Active', color: 'bg-green-600' },
  cut: { label: 'Cut', color: 'bg-red-600' },
  eligible: { label: 'Eligible', color: 'bg-slate-500' },
  ineligible: { label: 'Ineligible', color: 'bg-red-400' },
};

export default function RosterPanel({ teamId, orgId, seasonOverride, showExport = false, canManage = false }) {
  const { isDark } = useTheme();
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editJersey, setEditJersey] = useState(null);
  const [jerseyValue, setJerseyValue] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [seasonsLoaded, setSeasonsLoaded] = useState(false);

  // If the parent (Dashboard Roster tab) passes a `seasonOverride`, it always
  // wins — the team-level season dropdown defers to the page-level filter.
  // Falls back to the team's own most-recent roster season otherwise.
  useEffect(() => {
    if (seasonOverride) setSelectedSeason(seasonOverride);
  }, [seasonOverride]);

  // Fetch available seasons when team changes — reset season selection
  useEffect(() => {
    if (!teamId) return;
    setSeasonsLoaded(false);
    setRoster(null);
    setLoading(true);
    if (!seasonOverride) setSelectedSeason('');
    axios.get(`${API_URL}/api/roster/team/${teamId}/seasons`)
      .then(({ data }) => {
        const s = data.seasons || [];
        setSeasons(s);
        if (!seasonOverride && s.length > 0) setSelectedSeason(s[0]);
        setSeasonsLoaded(true);
      })
      .catch(() => { setSeasons([]); setSeasonsLoaded(true); });
  }, [teamId, seasonOverride]);

  const fetchRoster = useCallback(async () => {
    if (!teamId || !seasonsLoaded) return;
    try {
      const params = new URLSearchParams();
      if (filter) params.set('roster_type', filter);
      if (selectedSeason) params.set('season', selectedSeason);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const { data } = await axios.get(`${API_URL}/api/roster/team/${teamId}${qs}`);
      setRoster(data);
    } catch (err) {
      console.error('Failed to fetch roster:', err);
    } finally {
      setLoading(false);
    }
  }, [teamId, filter, selectedSeason, seasonsLoaded]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  const handleExport = (format) => {
    const rosterType = filter || 'active';
    window.open(`${API_URL}/api/exports/roster/${teamId}?format=${format}&roster_type=${rosterType}`, '_blank');
  };

  const handleScorebookExport = () => {
    window.open(`${API_URL}/api/exports/scorebook/${teamId}`, '_blank');
  };

  const handleCutPlayer = async (playerId) => {
    setActionLoading(playerId);
    try {
      await axios.post(`${API_URL}/api/roster/team/${teamId}/cut/${playerId}`);
      fetchRoster();
    } catch (err) {
      console.error('Cut failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivatePlayer = async (playerId) => {
    setActionLoading(playerId);
    try {
      await axios.post(`${API_URL}/api/roster/team/${teamId}/activate/${playerId}`);
      fetchRoster();
    } catch (err) {
      console.error('Activate failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddToRoster = async (playerId) => {
    setActionLoading(playerId);
    try {
      await axios.post(`${API_URL}/api/roster/team/${teamId}/add`, { player_id: playerId, status: 'tryout' });
      fetchRoster();
    } catch (err) {
      console.error('Add failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveJersey = async () => {
    if (!editJersey) return;
    setActionLoading(editJersey);
    try {
      await axios.put(`${API_URL}/api/roster/team/${teamId}/player/${editJersey}`, { jersey_number: parseInt(jerseyValue) || null });
      setEditJersey(null);
      fetchRoster();
    } catch (err) {
      console.error('Jersey update failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCsvImport = async () => {
    if (!csvText.trim()) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const lines = csvText.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(',').map(v => v.trim());
        if (vals.length < 2) continue;
        const row = {};
        headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
        rows.push(row);
      }
      const { data } = await axios.post(`${API_URL}/api/roster/team/${teamId}/import-csv`, { rows }, { withCredentials: true });
      setImportResult(data);
      if (data.imported > 0) fetchRoster();
    } catch (err) {
      setImportResult({ errors: [{ row: 0, reason: err.response?.data?.detail || 'Import failed' }] });
    } finally {
      setImportLoading(false);
    }
  };

  const handleSendWelcomeLetters = async () => {
    if (!window.confirm('Send welcome letters to all active roster players who haven\'t received one yet?')) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/roster/team/${teamId}/send-welcome-letters`);
      alert(`Welcome letters: ${data.sent} sent, ${data.total - data.sent} skipped (already sent or no family).`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send welcome letters');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => setCsvText(evt.target.result);
    reader.readAsText(file);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  if (!roster) {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-8 text-center">
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Select a team to view roster</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3">
            <div>
              <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <Users className="h-5 w-5 text-cyan-500" />
                {roster.team.name} Roster
              </CardTitle>
              <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {roster.count} players · Max age {roster.team.eligibility?.max_age} · ${roster.team.season_cost}/season
              </p>
            </div>
            {/* Season dropdown — hidden when the parent passes a
                `seasonOverride` (e.g. AD Roster page has its own
                page-level season filter). Avoids two pickers showing
                the same value. */}
            {!seasonOverride && seasons.length > 0 && (
              <select
                value={selectedSeason}
                onChange={(e) => { setSelectedSeason(e.target.value); setLoading(true); }}
                className={`text-sm rounded-md px-2 py-1 border cursor-pointer ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-700'}`}
                data-testid="roster-season-select"
              >
                {seasons.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Roster Status Counts */}
            <div className="flex gap-1">
              {['tryout', 'active', 'cut'].map((s) => (
                <Badge key={s} className={`cursor-pointer ${filter === s ? ROSTER_STATUS[s].color : isDark ? 'bg-slate-600' : 'bg-slate-200'}`} onClick={() => setFilter(filter === s ? '' : s)} data-testid={`filter-${s}`}>
                  {ROSTER_STATUS[s].label} {roster.counts?.[s] || 0}
                </Badge>
              ))}
            </div>
            {showExport && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => handleExport('csv')} className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid="export-roster-csv">
                  <Download className="h-3 w-3 mr-1" />CSV
                </Button>
                <Button size="sm" variant="outline" onClick={handleScorebookExport} className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid="export-scorebook">
                  <Download className="h-3 w-3 mr-1" />Stickers
                </Button>
              </div>
            )}
            {canManage && (
              <Button size="sm" variant="outline" onClick={handleSendWelcomeLetters} className={isDark ? 'border-slate-600 text-slate-300' : 'border-amber-300 text-amber-700'} data-testid="send-welcome-letters-btn">
                <Mail className="h-3 w-3 mr-1" />Welcome Letters
              </Button>
            )}
            {canManage && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setShowImport(true); setImportResult(null); setCsvText(''); }} data-testid="import-csv-btn">
                <Upload className="h-3 w-3 mr-1" />Import CSV
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {roster.players.length === 0 ? (
          <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No players {filter ? `with status "${filter}"` : 'on this roster yet'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <div className="min-w-[700px] space-y-2 px-2 sm:px-0">
              {/* Header */}
              <div className={`grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <div className="col-span-1">#</div>
                <div className="col-span-2">Name</div>
                <div className="col-span-1">Age/Yr</div>
                <div className="col-span-1">Sex</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1">Health</div>
                <div className="col-span-1">Elig.</div>
                {canManage && <div className="col-span-3">Actions</div>}
              </div>
              {roster.players.map((player) => {
                const rStatus = ROSTER_STATUS[player.roster_status] || ROSTER_STATUS.eligible;
                return (
                  <div key={player.id} className={`grid grid-cols-12 gap-2 items-center px-3 py-3 rounded-lg ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-slate-50 hover:bg-slate-100'}`} data-testid={`roster-player-${player.id}`}>
                    <div className="col-span-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer ${isDark ? 'bg-cyan-600 text-white' : 'bg-cyan-100 text-cyan-700'}`} onClick={() => { if (canManage) { setEditJersey(player.id); setJerseyValue(player.jersey_number || ''); } }} title={canManage ? 'Click to edit' : ''}>
                        {player.jersey_number || '-'}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {player.first_name} {player.last_name}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{player.family_name}</p>
                    </div>
                    <div className="col-span-1">
                      <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {player.calculated_age != null ? player.calculated_age : (player.year || '--')}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <Badge className={['F','female'].includes(player.gender) ? 'bg-pink-600' : 'bg-blue-600'}>
                        {['F','female'].includes(player.gender) ? 'F' : 'M'}
                      </Badge>
                    </div>
                    <div className="col-span-2">
                      <Badge className={rStatus.color}>{rStatus.label}</Badge>
                    </div>
                    <div className="col-span-1">
                      {player.health_status?.is_healthy ? (
                        <Heart className="h-4 w-4 text-green-500" />
                      ) : (
                        <Heart className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    <div className="col-span-1">
                      {player.eligible === true ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : player.eligible === false ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`} title="No DOB on file">--</span>
                      )}
                    </div>
                    {canManage && (
                      <div className="col-span-3 flex gap-1">
                        {player.roster_status === 'eligible' && (
                          <Button size="sm" variant="outline" className="text-xs px-2 py-1 h-7" onClick={() => handleAddToRoster(player.id)} disabled={actionLoading === player.id} data-testid={`add-roster-${player.id}`}>
                            <UserCheck className="h-3 w-3 mr-1" />Add
                          </Button>
                        )}
                        {player.roster_status === 'tryout' && (
                          <>
                            <Button size="sm" className="text-xs px-2 py-1 h-7 bg-green-600 hover:bg-green-700" onClick={() => handleActivatePlayer(player.id)} disabled={actionLoading === player.id} data-testid={`activate-${player.id}`}>
                              <UserCheck className="h-3 w-3 mr-1" />Select
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs px-2 py-1 h-7 border-red-500 text-red-400" onClick={() => handleCutPlayer(player.id)} disabled={actionLoading === player.id} data-testid={`cut-${player.id}`}>
                              <UserMinus className="h-3 w-3 mr-1" />Cut
                            </Button>
                          </>
                        )}
                        {player.roster_status === 'active' && (
                          <Button size="sm" variant="outline" className="text-xs px-2 py-1 h-7 border-red-500 text-red-400" onClick={() => handleCutPlayer(player.id)} disabled={actionLoading === player.id} data-testid={`cut-${player.id}`}>
                            <UserMinus className="h-3 w-3 mr-1" />Cut
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>

      {/* Edit Jersey Dialog */}
      <Dialog open={!!editJersey} onOpenChange={() => setEditJersey(null)}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Hash className="h-5 w-5 text-cyan-500" />Assign Jersey Number
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input type="number" min="0" max="99" value={jerseyValue} onChange={(e) => setJerseyValue(e.target.value)} placeholder="Jersey number" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="jersey-input" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditJersey(null)}>Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={handleSaveJersey} disabled={actionLoading === editJersey} data-testid="save-jersey-btn">
                {actionLoading === editJersey ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <FileSpreadsheet className="h-5 w-5 text-emerald-500" />Bulk Import Players (CSV)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Upload a CSV or paste data. Required columns: <code>first_name, last_name</code>. Optional: <code>birthday, gender, jersey_number</code>.
            </p>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Upload CSV File</label>
              <Input type="file" accept=".csv" onChange={handleFileUpload} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="csv-file-input" />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Or Paste CSV Data</label>
              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                rows={6}
                placeholder={`first_name,last_name,birthday,gender,jersey_number\nJohn,Doe,2012-05-15,male,23\nJane,Smith,2013-01-10,female,7`}
                className={`w-full rounded-md border p-2 text-sm font-mono ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'border-slate-300 placeholder-slate-400'}`}
                data-testid="csv-textarea"
              />
            </div>
            {importResult && (
              <div className={`rounded-md p-3 text-sm ${importResult.imported > 0 ? (isDark ? 'bg-emerald-900/30 border border-emerald-800 text-emerald-300' : 'bg-emerald-50 border border-emerald-200 text-emerald-800') : (isDark ? 'bg-red-900/30 border border-red-800 text-red-300' : 'bg-red-50 border border-red-200 text-red-800')}`} data-testid="import-result">
                {importResult.imported > 0 && <p>{importResult.imported} player(s) imported successfully.</p>}
                {importResult.skipped > 0 && <p>{importResult.skipped} skipped (duplicates or errors).</p>}
                {importResult.errors?.length > 0 && (
                  <ul className="mt-1 list-disc list-inside">
                    {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
                    {importResult.errors.length > 5 && <li>...and {importResult.errors.length - 5} more</li>}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowImport(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleCsvImport} disabled={importLoading || !csvText.trim()} data-testid="import-csv-submit">
                {importLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
